import React, { useMemo, useCallback } from 'react';
import { Flex, Spinner } from '@patternfly/react-core';
import { useTranslation } from '../../../../localization/useTranslation';
import ModelType from '../../../Model/ModelType';
import './DialPanel.scss';
import T from '../../../../localization/LocalizationKeys';
import Icons, { Size } from '../../../Common/Icons';
import _ from 'lodash';
import useInterval from '../../../../utils/hooks/useInterval';
import dataSources from '../../datasources';
import { SortDirection } from '../../../Common/SimpleTable/SimpleTable';
import { sortLocale } from '../../../../utils/string';
import DialPanelList from './DialPanelList';
import initialOptions from './intialOptions';
import fixedOptions from './fixedOptions';


const DEFAULT_REFRESH_INTERVAL = 5; // 5 minutes
const MIN_REFRESH_INTERVAL = 5; // 5 minutes
const DEFAULT_HEIGHT = 150;
const DEFAULT_DECIMALS = 2;
const DEFAULT_FORMATTER = `<div style="text-align:center">
<span style="font-size:12px">{y}</span><br/>
<span style="font-size:12px;opacity:0.4">{{unit}}</span><br/>
<span style="font-size:12px;opacity:0.4">at {{timestamp}}</span>
</div>
`;

export const SELECTABLE_DATA_SOURCES = Object.freeze(_.map(_.pick(dataSources, ['realTimeTelemetry', 'lastValue']), ({ label }, type) => ({ type, label })));
// Here we select the default value for dataSource property
// TODO: use const for lastValue, or maybe use a default: true in the list of selectable data source.
export const DEFAULT_DATA_SOURCE = _.find(SELECTABLE_DATA_SOURCES, { type: 'lastValue' });
const GROUP_BY = Object.freeze([
  {
    value: 'bySignal',
    text: T.common.groupBy.signal
  },
  {
    value: 'byAsset',
    text: T.common.groupBy.asset
  }
]);
const DEFAULT_GROUP_BY = _.find(GROUP_BY, { value: 'byAsset' });

const SORT_BY = Object.freeze([
  {
    value: SortDirection.ASC,
    text: T.common.sortBy.ascending
  },
  {
    value: SortDirection.DESC,
    text: T.common.sortBy.descending
  }
]);
const DEFAULT_SORT_BY = _.find(SORT_BY, { value: SortDirection.ASC });


// TODO fix the timezone offset so that it is calculated everytime instead as a option initially
const LastValueDialPanelList = ({ data, api }) => {

  const { refreshInterval, hasError, isLoading, signalData } = data.signalValues;
  const plotlines = data.plotlineValues?.plotlines;

  useInterval(api.reload, Math.max(MIN_REFRESH_INTERVAL, (refreshInterval ?? DEFAULT_REFRESH_INTERVAL) * 60000));

  const T = useTranslation();
  const loadingDescription = hasError ? T.common.error.generic : T.common.loading;
  const options = useMemo(() => {
    return _.merge({}, data.options ?? initialOptions, fixedOptions, { time: { timezoneOffset: new Date().getTimezoneOffset() } });
  }, [data.options]);

  const sortItems = useCallback((items, key) => {
    const result = sortLocale(_.uniqBy(items, key), key);
    return (data?.sortBy === SortDirection.DESC) ? _.reverse(result) : result;
  }, [data?.sortBy]);

  const groupByAsset = useCallback(() => {
    const assets = sortItems(_.uniqBy(signalData, 'assetId'), 'assetDisplayName');
    return (_.map(assets, asset => {
      const signals = _.filter(signalData, ['assetId', asset.assetId]);
      return (
        <DialPanelList
          header={asset.assetDisplayName}
          signalData={sortItems(signals, 'signalDisplayName')}
          isLoading={isLoading}
          dialOptions={options}
          gaugeHeight={data?.gaugeHeight ?? DEFAULT_HEIGHT}
          valueFormatter={data?.valueFormatter || DEFAULT_FORMATTER}
          plotlines={plotlines}
        />
      );
    })

    );
  }, [data?.gaugeHeight, data?.valueFormatter, isLoading, options, plotlines, signalData, sortItems]);

  const groupBySignal = useCallback(() => {
    const signals = sortItems(_.uniqBy(signalData, 'signalId'), 'signalDisplayName');
    return (_.map(signals, signal => {
      const assets = _.filter(signalData, ['signalId', signal.signalId]);
      return (
        <DialPanelList
          header={signal.signalDisplayName}
          signalData={sortItems(assets, 'assetDisplayName')}
          isLoading={isLoading}
          dialOptions={options}
          gaugeHeight={data?.gaugeHeight ?? DEFAULT_HEIGHT}
          valueFormatter={data?.valueFormatter || DEFAULT_FORMATTER}
          plotlines={plotlines}
        />
      );
    }));

  }, [data?.gaugeHeight, data?.valueFormatter, isLoading, options, plotlines, signalData, sortItems]);

  const lastValueTelemetry = useCallback(() => {
    if (_.isEmpty(signalData)) {
      return (<>No Data</>);
    }

    if (!_.isEmpty(signalData.dataPoints)) {
      return (<></>);
    }

    switch (data?.groupBy) {
      case 'byAsset':
        return groupByAsset();
      case 'bySignal':
        return groupBySignal();
      default:
        return groupByAsset();
    }
  }, [data?.groupBy, groupByAsset, groupBySignal, signalData]);

  return (
    <div >
      { (isLoading || hasError) && <Flex alignItems={{ default: 'alignItemsCenter' }}>
        <Spinner size="md" />
        <span>{ loadingDescription }</span>
      </Flex> }
      { lastValueTelemetry() }
    </div>
  );

};

LastValueDialPanelList.models = [
  {
    path: 'sortBy',
    label: T.common.sortBy.label,
    type: ModelType.OPTIONSV2,
    options: SORT_BY,
    placeholder: DEFAULT_SORT_BY.text
  },
  {
    path: 'groupBy',
    label: T.common.groupBy.label,
    type: ModelType.OPTIONSV2,
    options: GROUP_BY,
    placeholder: DEFAULT_GROUP_BY.text
  },
  {
    type: ModelType.JSON_EDIT_DIALOG,
    path: 'options',
    label: T.dashboard.editor.inputs.json.options,
    properties: {
      height: '380px',
      width: '90%',
      initialValue: initialOptions
    }
  },
  {
    type: ModelType.NUMBER,
    path: 'gaugeHeight',
    label: 'height',
    placeholder: DEFAULT_HEIGHT
  },
  {
    type: ModelType.NUMBER,
    path: 'decimals',
    label: 'Decimals',
    placeholder: DEFAULT_DECIMALS
  },
  {
    type: ModelType.TEXTAREA,
    path: 'valueFormatter',
    label: 'Value Format',
    properties: {
      initialValue: DEFAULT_FORMATTER
    }
  }
];


LastValueDialPanelList.info = {
  label: T.dashboard.panels.types.lastValueDialList,
  icon: <Icons.Dial size={Size.LARGE}/>
};

LastValueDialPanelList.properties = panelProperties => {
  const modifiedProps = {
    signalValues: {
      type: panelProperties?.dataSource ?? DEFAULT_DATA_SOURCE.type
    },
    plotlineValues: {
      type: 'plotlines'
    }
  };
  return modifiedProps;
};


export default React.memo(LastValueDialPanelList);
